import { useState, useEffect, useMemo, useRef } from "react";
import {
  ChevronLeft, ChevronRight,
  TrendingUp, ShoppingCart, AlertCircle, BarChart2,
  Loader2, ArrowUpDown, FileDown, Search, X, FileText, MapPin, Store,
} from "lucide-react";
import { Select, DateRangePicker, Modal, OrderBadges } from "../../../components";
import axios from "axios";
import { GET_MANAGER_ROUTES_URL, GET_ALL_ORDERS_FOR_EXPORT_URL, GET_ORDER_BALANCE_SNAPSHOT_URL } from "../../../features/UrlHelper";
import {
  ITEM_TYPE_META,
  ORDER_STATUS_META,
  PAYMENT_TYPE_LABELS,
} from "../../../features/orderConstants";
import { getComposedMenus, getPriceListTag, getOrderMapsLink } from "../../../features/orderUtils";
import OrderItemsModal from "../../../components/OrderItemsModal/OrderItemsModal";
import { useOverviewStats } from "./useOverviewStats";
import {
  DATE_PRESETS, toISODate, shiftISODate, getPresetRange, getActivePresetKey,
} from "./overviewDatePresets";
import { KpiCardFontProvider, useKpiCardFontSize } from "./KpiCardFontContext";
import { DynamicFontValue } from "./useResizingFontSize";
import OverviewInsights from "./OverviewInsights";
import "./DashboardSection.scss";
import "./OverviewSection.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const SALES_TABS = Object.entries(ITEM_TYPE_META)
  .sort(([, a], [, b]) => a.order - b.order)
  .map(([type, { labelSingular, Icon }]) => ({ type, label: labelSingular, Icon }));
// ─── Helpers ──────────────────────────────────────────────────────────────────
function calculateRemainingDebt(currentOrder, allOrders) {
  if (!allOrders || allOrders.length === 0) return Number(currentOrder.balance) || 0;

  const customerHistory = allOrders.filter(
    (o) =>
      o.customer_phone === currentOrder.customer_phone &&
      new Date(o.delivery_date) <= new Date(currentOrder.delivery_date)
  );

  return customerHistory.reduce((sum, o) => sum + (Number(o.balance) || 0), 0);
}

// ─── ReadOnlyOrderCard ────────────────────────────────────────────────────────
function ReadOnlyOrderCard({ order }) {
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [balanceSnapshot, setBalanceSnapshot] = useState(null);
  const composedMenus = getComposedMenus(order.items || []);
  const meta = ORDER_STATUS_META[order.status] || ORDER_STATUS_META.ACCEPTED;
  const StatusIcon = meta.Icon;
  const hasItems = (order.items || []).filter((i) => i.quantity > 0).length > 0;
  const priceTag = getPriceListTag(order.kod_cennik);

  useEffect(() => {
    if (hasItems) return;
    axios
      .get(GET_ORDER_BALANCE_SNAPSHOT_URL(order.id))
      .then(({ data }) => setBalanceSnapshot(data.balance_snapshot))
      .catch(() => setBalanceSnapshot(null));
  }, [order.id, hasItems]);
  const createdAt = order.created_at ? new Date(order.created_at) : null;
  const createdDate = createdAt
    ? createdAt.toLocaleDateString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric" })
    : null;
  const createdTime = createdAt
    ? createdAt.toLocaleTimeString("sk-SK", { hour: "2-digit", minute: "2-digit" })
    : null;

  const { url: mapsUrl, hasPreciseLocation } = getOrderMapsLink(order);

  return (
    <div className={`order-card order-card--${order.status?.toLowerCase()}`}>
      {/* ── Header ── */}
      <div className="order-card__header">
        <div className="order-card__header-left">
          {order.address_code && (
            <span className="order-card__code">{order.address_code}</span>
          )}
          {mapsUrl ? (
            <a
              className="order-card__address order-card__address--link"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Otvoriť v Google Mapy"
              onClick={(e) => e.stopPropagation()}
            >
              {order.delivery_address || "—"}
              {hasPreciseLocation && (
                <MapPin size={13} className="order-card__address-pin" title="Spresnená poloha" />
              )}
            </a>
          ) : (
            <span className="order-card__address">{order.delivery_address || "—"}</span>
          )}
        </div>
        <div className="order-card__header-right">
          {(createdDate || createdTime) && (
            <div className="order-card__created-datetime">
              {createdDate && <span className="order-card__created-date">{createdDate}</span>}
              {createdTime && <span className="order-card__created-time">{createdTime}</span>}
            </div>
          )}
          <span className={`order-card__price-tag${priceTag.isDefault ? " order-card__price-tag--default" : ""}`} title="Cenník objednávky">
            {priceTag.label}
          </span>
          <span className="order-card__price">
            {Number(order.total_price || 0).toFixed(2)} €
          </span>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="order-card__body">
        {/* Ľavý blok: zákazník */}
        <div className="order-card__col-customer">
          <span className="order-card__customer-name">
            {[order.customer_name, order.customer_surname].filter(Boolean).join(" ") || "—"}
          </span>
          {/* Odznaky a spresnenie adresy patria sem, nie do hlavičky: tam sa
              na mobile bili s adresou a adresa je to prvé, čo človek číta. */}
          <OrderBadges order={order} />
          {order.address_note && (
            <span className="order-card__address-note">{order.address_note}</span>
          )}
          {order.customer_phone && (
            <a href={`tel:${order.customer_phone}`} className="order-card__customer-phone">
              {order.customer_phone}
            </a>
          )}
          {order.customer_note && (
            <span className="order-card__customer-note">{order.customer_note}</span>
          )}
        </div>

        {/* Stredný blok: obsah */}
        <div className="order-card__col-items">
          {!hasItems ? (
            <span className="order-card__debt-payment">
              Vyplatenie dlhu
              <span className="order-card__debt-amount">
                {" "}— Splatil: {Number(order.balance || 0).toFixed(2)} € — Zostávajúci dlh: {balanceSnapshot !== null ? Math.abs(balanceSnapshot).toFixed(2) : "…"} €
              </span>
            </span>
          ) : (
            <span
              className="order-card__items order-card__items--clickable"
              onClick={() => setItemsModalOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setItemsModalOpen(true)}
              title="Zobraziť detail položiek"
            >
              {(order.items || [])
                .filter((i) => i.quantity > 0)
                .map((item, idx) => (
                  <span key={idx} className="order-card__item-chip">
                    {item.quantity}x{" "}
                    <strong
                      className={`order-card__item-code${!item.menu_code ? " order-card__item-code--truncated" : ""}`}
                      title={item.name}
                    >
                      {item.menu_code || item.name}
                    </strong>
                  </span>
                ))}
            </span>
          )}
          {/* Dočasne skryté zložené menu; kód zostáva pripravený na opätovné aktivovanie.
          {composedMenus.length > 0 && (
            <div
              className="order-card__composed-menus order-card__composed-menus--clickable"
              onClick={() => setItemsModalOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setItemsModalOpen(true)}
            >
              {composedMenus.map(({ label, count, soupName, mainName }) => (
                <span
                  key={label}
                  className="order-card__composed-badge"
                  title={`${soupName} + ${mainName}`}
                >
                  <span className="order-card__composed-count">{count}×</span>
                  <strong className="order-card__composed-label">{label}</strong>
                </span>
              ))}
            </div>
          )}
          */}
          {order.dispatcher_note && (
            <span className="order-card__note">{order.dispatcher_note}</span>
          )}
        </div>

        {/* Pravý blok: badges (read-only) */}
        <div className="order-card__col-actions">
          <div className="ovw__badge-group">
            <span className="ovw__badge-label">Stav:</span>
            <span
              className="order-card__status-badge"
              style={{ "--status-color": meta.color, "--status-rgb": meta.rgb }}
            >
              <StatusIcon size={12} />
              <span>{meta.label}</span>
            </span>
          </div>
          {order.route_name && (
            <div className="ovw__badge-group">
              <span className="ovw__badge-label">Trasa:</span>
              <span
                className="ovw__order-route-badge"
                style={
                  order.route_color
                    ? {
                        backgroundColor: order.route_color + "22",
                        borderColor: order.route_color + "66",
                        color: order.route_color,
                      }
                    : undefined
                }
              >
                {order.route_color && (
                  <span
                    className="order-card__route-dot"
                    style={{ backgroundColor: order.route_color }}
                  />
                )}
                {order.route_name}
              </span>
            </div>
          )}
          {order.status === 'DELIVERED' && order.payment_type && (
            <div className="ovw__badge-group">
              <span className="ovw__badge-label">Platba:</span>
              <span
                className={`ovw__payment-badge ovw__payment-badge--${order.payment_type.toLowerCase()}`}
              >
                {PAYMENT_TYPE_LABELS[order.payment_type] || order.payment_type}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Items detail modal */}
      <OrderItemsModal
        isOpen={itemsModalOpen}
        onClose={() => setItemsModalOpen(false)}
        order={order}
      />
    </div>
  );
}

// Zdroj objednávky (R3a) a spôsob odberu (R52). Oboje má len tri stavy,
// takže voľby sú napísané priamo — nie je z čoho ich odvodzovať.
const SOURCE_FILTER_OPTIONS = [
  { value: "all",   label: "Všetky" },
  { value: "LOCAL", label: "Prevádzka" },
  { value: "WEB",   label: "Web" },
];

const DELIVERY_TYPE_FILTER_OPTIONS = [
  { value: "all",      label: "Všetky" },
  { value: "DELIVERY", label: "Donáška" },
  { value: "PICKUP",   label: "Na prevádzke" },
];

// ─── OverviewSection ──────────────────────────────────────────────────────────
function OverviewSection() {
  const [startDate, setStartDate]           = useState(() => toISODate(new Date()));
  const [endDate, setEndDate]               = useState(() => toISODate(new Date()));
  const [selectedRoute, setSelectedRoute]   = useState("all");
  const [selectedPaymentFilter, setSelectedPaymentFilter] = useState("all");
  // Zdroj (R3a) a spôsob odberu (R52). Oboje filtruje až zoznam — trasa
  // a obdobie sa riesia vyssie a tahaju sa z nich aj KPI.
  const [sourceFilter, setSourceFilter] = useState("all");
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery]       = useState("");
  const [routes, setRoutes]                 = useState([]);
  const [activeSalesTab, setActiveSalesTab] = useState("SOUP");
  const [salesSortDir, setSalesSortDir]     = useState("desc");
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);

  const { orders, loading, error, kpi, salesByType, hourlyData } =
    useOverviewStats(startDate, endDate, selectedRoute);

  const maxHourlyOrders = useMemo(
    () => Math.max(...hourlyData.map((d) => d.orders), 1),
    [hourlyData]
  );

  useEffect(() => {
    axios
      .get(GET_MANAGER_ROUTES_URL)
      .then((res) => setRoutes(res.data))
      .catch(() => {});
  }, []);

  const routeOptions = useMemo(
    () => [
      {
        value: "all",
        label: (
          <span className="drs__route-option">
            <span className="drs__route-dot" style={{ background: "var(--c-stav-vsetky)" }} />
            Všetky trasy
          </span>
        ),
      },
      ...routes.map((r) => ({
        value: String(r.id),
        label: (
          <span className="drs__route-option">
            <span className="drs__route-dot" style={{ background: r.color_code }} />
            {r.name}
          </span>
        ),
      })),
    ],
    [routes]
  );

  const sortedOrders = useMemo(
    () =>
      [...orders].sort((a, b) =>
        a.created_at && b.created_at
          ? new Date(a.created_at) - new Date(b.created_at)
          : a.id - b.id
      ),
    [orders]
  );

  const paymentFilterOptions = useMemo(() => {
    const discoveredTypes = new Set(sortedOrders.map((order) => order.payment_type).filter(Boolean));
    const knownTypes = Object.keys(PAYMENT_TYPE_LABELS);

    const knownOptions = knownTypes
      .filter((type) => discoveredTypes.has(type))
      .map((type) => ({
        value: type,
        label: PAYMENT_TYPE_LABELS[type] || type,
      }));

    const unknownOptions = Array.from(discoveredTypes)
      .filter((type) => !knownTypes.includes(type))
      .sort((a, b) => a.localeCompare(b))
      .map((type) => ({ value: type, label: type }));

    return [
      { value: "all", label: "Všetky platby" },
      ...knownOptions,
      ...unknownOptions,
    ];
  }, [sortedOrders]);

  const normalizedSearchQuery = useMemo(
    () => searchQuery.toLowerCase().trim(),
    [searchQuery]
  );

  const filteredOrders = useMemo(
    () =>
      sortedOrders.filter((order) => {
        const matchesPayment = selectedPaymentFilter === "all"
          ? true
          : order.payment_type === selectedPaymentFilter;

        const matchesSource = sourceFilter === "all"
          ? true
          : (order.source || "LOCAL") === sourceFilter;

        const matchesDeliveryType = deliveryTypeFilter === "all"
          ? true
          : (order.delivery_type || "DELIVERY") === deliveryTypeFilter;

        const customerFullName = [order.customer_name, order.customer_surname]
          .filter(Boolean)
          .join(" ");

        const searchableFields = [
          order.address_code,
          order.delivery_address,
          order.address,
          order.customer_name,
          order.customer_surname,
          customerFullName,
          order.customer_phone,
          order.phone,
        ]
          .filter((value) => value !== null && value !== undefined)
          .map((value) => String(value).toLowerCase());

        const matchesSearch = !normalizedSearchQuery
          || searchableFields.some((value) => value.includes(normalizedSearchQuery));

        return matchesPayment && matchesSource && matchesDeliveryType && matchesSearch;
      }),
    [sortedOrders, selectedPaymentFilter, sourceFilter, deliveryTypeFilter, normalizedSearchQuery]
  );

  // Súhrn objednaných jedál za celý zobrazený výber (rešpektuje dátumový rozsah,
  // trasu aj filtre zoznamu). Zrušené objednávky sa nezapočítavajú – rovnako
  // ako v predajnosti denného menu.
  const orderedItemsSummary = useMemo(() => {
    const aggregated = new Map();

    for (const order of filteredOrders) {
      if (order.status === "CANCELLED") continue;
      for (const item of order.items || []) {
        const quantity = Number(item.quantity || 0);
        if (quantity <= 0) continue;

        const label = item.menu_code || item.name || "—";
        const key = item.daily_menu_id ?? `label:${label}`;
        const existing = aggregated.get(key);

        if (existing) {
          existing.quantity += quantity;
        } else {
          aggregated.set(key, {
            key,
            label,
            name: item.name || "",
            typeKey: (item.item_type || "").toUpperCase(),
            hasCode: Boolean(item.menu_code),
            quantity,
          });
        }
      }
    }

    return [...aggregated.values()].sort((a, b) => {
      const orderA = ITEM_TYPE_META[a.typeKey]?.order ?? 99;
      const orderB = ITEM_TYPE_META[b.typeKey]?.order ?? 99;
      if (orderA !== orderB) return orderA - orderB;
      return a.label.localeCompare(b.label, "sk", { numeric: true });
    });
  }, [filteredOrders]);

  // Syntetická "objednávka" pre OrderItemsModal – zdieľa rovnaký detail
  // položiek ako jednotlivé objednávky.
  const orderedItemsSummaryOrder = useMemo(
    () => ({
      items: orderedItemsSummary.map((item) => ({
        quantity: item.quantity,
        menu_code: item.hasCode ? item.label : null,
        name: item.name || item.label,
        item_type: item.typeKey,
      })),
    }),
    [orderedItemsSummary]
  );

  const currentSalesItems = salesByType[activeSalesTab] || [];

  const handleExportToExcel = async () => {
    try {
      const XLSX = await import("xlsx");
      const [startDay, startMonth, startYear] = startDate.split("-").reverse();
      const [endDay, endMonth, endYear] = endDate.split("-").reverse();
      const fileDatePart = startDate === endDate
        ? `${startDay}_${startMonth}_${startYear}`
        : `${startDay}_${startMonth}_${startYear}_az_${endDay}_${endMonth}_${endYear}`;

      const { data: allOrders } = await axios.get(GET_ALL_ORDERS_FOR_EXPORT_URL);

      const exportData = sortedOrders.map((order) => ({
        'Dátum doručenia': order.delivery_date
          ? new Date(order.delivery_date).toLocaleDateString("sk-SK")
          : "",
        'Čas doručenia': order.delivered_at
          ? new Date(order.delivered_at).toLocaleTimeString("sk-SK", { hour: '2-digit', minute: '2-digit' })
          : "",
        'Zákazník': [order.customer_name, order.customer_surname].filter(Boolean).join(" ") || "",
        'Telefón': order.customer_phone || "",
        'Adresa': order.delivery_address || order.address_code || "",
        'Jedlá': (order.items || [])
          .filter((i) => i.quantity > 0)
          .map((i) => `${i.quantity}x ${i.menu_code || i.name}`)
          .join(", "),
        'Poznámka zákazníka': order.customer_note || "",
        // Premenované: pole používa aj zákazník pri webovej objednávke,
        // takže "dispečera" už nesedelo.
        'Poznámka k objednávke': order.dispatcher_note || "",
        'Trasa': order.route_name || "",
        'Zdroj': (order.source || "LOCAL") === "WEB" ? "Web" : "Prevádzka",
        'Spôsob': (order.delivery_type || "DELIVERY") === "PICKUP" ? "Na prevádzke" : "Donáška",
        'Suma (€)': Number(order.total_price || 0).toFixed(2),
        'Zaplatené (€)': Number(order.amount_paid || 0).toFixed(2),
        'Zostatkový dlh (€)': calculateRemainingDebt(order, allOrders),
        'Stav': ORDER_STATUS_META[order.status]?.label || order.status || "",
        'Platba': PAYMENT_TYPE_LABELS[order.payment_type] || order.payment_type || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Autofilter na celý hlavičkový riadok. Šírka sa počíta z počtu stĺpcov,
      // nie natvrdo — s pribudnutím "Zdroj" a "Spôsob" už pevné "A1:N1"
      // posledné dva stĺpce vynechávalo.
      const poslednyStlpec = XLSX.utils.encode_col(Math.max(Object.keys(exportData[0] || {}).length - 1, 0));
      worksheet['!autofilter'] = { ref: `A1:${poslednyStlpec}1` };

      // Dynamická šírka stĺpcov podľa obsahu
      const colKeys = Object.keys(exportData[0] || {});
      worksheet['!cols'] = colKeys.map((key) => {
        const maxLen = exportData.reduce((max, row) => {
          return Math.max(max, String(row[key] ?? "").length);
        }, key.length);
        return { wch: Math.min(maxLen + 2, 60) };
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Objednávky");
      XLSX.writeFile(workbook, `objednavky_${fileDatePart}.xlsx`);
    } catch (err) {
      console.error("Export zlyhal:", err);
    }
  };

  const handleShiftRange = (dayOffset) => {
    setStartDate((prev) => shiftISODate(prev, dayOffset));
    setEndDate((prev) => shiftISODate(prev, dayOffset));
  };

  const handleRangeChange = ({ startDate: nextStartDate, endDate: nextEndDate }) => {
    if (!nextStartDate || !nextEndDate) return;
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
  };

  const handlePresetClick = (key) => {
    const range = getPresetRange(key);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const activePresetKey = getActivePresetKey(startDate, endDate);

  const sortedSalesItems = useMemo(
    () => [...currentSalesItems].sort((a, b) =>
      salesSortDir === "desc" ? b.sold - a.sold : a.sold - b.sold
    ),
    [currentSalesItems, salesSortDir]
  );

  const maxSold = sortedSalesItems.length > 0
    ? Math.max(...sortedSalesItems.map((i) => i.sold), 1)
    : 1;

  return (
    <KpiCardFontProvider>
      <div className="ovw">
      {/* ── Zaujímavosti za celú históriu — nad filtrom, lebo od neho nezávisia ── */}
      <OverviewInsights />

      {/* ── Filter bar ── */}
      <div className="dm__filter ovw__filter">
        <div className="ovw__presets" role="group" aria-label="Rýchly výber obdobia">
          {DATE_PRESETS.map(({ key, label, title }) => (
            <button
              key={key}
              type="button"
              className={`ovw__tab${activePresetKey === key ? " ovw__tab--active" : ""}`}
              onClick={() => handlePresetClick(key)}
              aria-pressed={activePresetKey === key}
              title={title}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          className="dm__week-nav"
          onClick={() => handleShiftRange(-1)}
          title="Posunúť obdobie o deň dozadu"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="dm__filter-pickers">
          <DateRangePicker
            label="Obdobie"
            startLabel="Od"
            endLabel="Do"
            value={{ startDate, endDate }}
            onChange={handleRangeChange}
          />
        </div>
        <button
          className="dm__week-nav"
          onClick={() => handleShiftRange(1)}
          title="Posunúť obdobie o deň dopredu"
        >
          <ChevronRight size={18} />
        </button>
        <div className="dm__filter-pickers drs__picker-cell drs__route-picker">
          <Select
            label="Trasa"
            options={routeOptions}
            value={selectedRoute}
            onChange={setSelectedRoute}
          />
        </div>
      </div>

      {/* ── Chybová hláška ── */}
      {error && (
        <div className="ovw__error">
          <AlertCircle size={15} />
          <span>{error}</span>
        </div>
      )}

      {/* ── KPI Grid ── */}
      <div className="ovw__kpi-grid">
        <div className="ovw__kpi-card ovw__kpi-card--primary">
          <div className="ovw__kpi-top">
            <div className="ovw__kpi-icon ovw__kpi-icon--on-primary">
              <TrendingUp size={22} />
            </div>
            <div className="ovw__kpi-values">
              <DynamicFontValue
                cardId="revenue-card"
                value={loading ? "…" : `${kpi.totalRevenue.toFixed(2)} €`}
                count=""
                colorClass="ovw__kpi-value--on-primary"
                measureText={`${kpi.totalRevenue?.toFixed(2) || "0.00"} €`}
              />
            </div>
          </div>
          <div className="ovw__kpi-body">
            <span className="ovw__kpi-label ovw__kpi-label--on-primary">Tržba z rozvozu (cash)</span>
          </div>
        </div>
        {/* Tržba na prevádzke (R52). Stojí hneď vedľa tržby z rozvozu, lebo
            sa s ňou porovnáva — a do nej sa zámerne neráta, aby sa sčítaním
            tržba nezdvojila. */}
        <div className="ovw__kpi-card">
          <div className="ovw__kpi-top">
            <div className="ovw__kpi-icon ovw__kpi-icon--secondary">
              <Store size={22} />
            </div>
            <div className="ovw__kpi-values">
              <DynamicFontValue
                cardId="pickup-card"
                value={loading ? "…" : `${kpi.pickupRevenue.toFixed(2)} €`}
                count={loading ? "" : `(${kpi.pickupOrdersCount} ks)`}
                colorClass="ovw__kpi-value--secondary"
                measureText={`${kpi.pickupRevenue?.toFixed(2) || "0.00"} €`}
              />
            </div>
          </div>
          <div className="ovw__kpi-body">
            <span className="ovw__kpi-label">Tržba na prevádzke</span>
          </div>
        </div>
        <div className="ovw__kpi-card">
          <div className="ovw__kpi-top">
            <div className="ovw__kpi-icon ovw__kpi-icon--danger">
              <AlertCircle size={22} />
            </div>
            <div className="ovw__kpi-values">
              <DynamicFontValue
                cardId="debt-card"
                value={loading ? "…" : `${kpi.totalDebt.toFixed(2)} €`}
                count={loading ? "" : `(${kpi.debtOrdersCount} ks)`}
                colorClass="ovw__kpi-value--danger"
                measureText={`${kpi.totalDebt?.toFixed(2) || "0.00"} €`}
              />
            </div>
          </div>
          <div className="ovw__kpi-body">
            <span className="ovw__kpi-label">Neuhradené / Dlh</span>
          </div>
        </div>
        <div className="ovw__kpi-card">
          <div className="ovw__kpi-top">
            <div className="ovw__kpi-icon ovw__kpi-icon--secondary">
              <FileText size={22} />
            </div>
            <div className="ovw__kpi-values">
              <DynamicFontValue
                cardId="invoice-card"
                value={loading ? "…" : `${kpi.totalInvoice.toFixed(2)} €`}
                count={loading ? "" : `(${kpi.invoiceOrdersCount} ks)`}
                colorClass="ovw__kpi-value--secondary"
                measureText={`${kpi.totalInvoice?.toFixed(2) || "0.00"} €`}
              />
            </div>
          </div>
          <div className="ovw__kpi-body">
            <span className="ovw__kpi-label">Na faktúru</span>
          </div>
        </div>
        <div className="ovw__kpi-card">
          <div className="ovw__kpi-top">
            <div className="ovw__kpi-icon">
              <ShoppingCart size={22} />
            </div>
            <div className="ovw__kpi-values">
              <DynamicFontValue
                cardId="orders-count-card"
                value={loading ? "…" : `${kpi.ordersCount}`}
                count=""
                colorClass=""
                measureText={`${kpi.ordersCount || "0"}`}
              />
            </div>
          </div>
          <div className="ovw__kpi-body">
            <span className="ovw__kpi-label">Počet objednávok</span>
          </div>
        </div>
      </div>

      {/* ── Grafy: Predajnosť + Vývoj objednávok ── */}
      <div className="ovw__charts-grid">

      {/* ── Predajnosť denného menu ── */}
      <div className="ovw__panel ovw__panel--chart ovw__panel--chart-sales">
        <div className="ovw__panel-header ovw__panel-header--with-action">
          <h3 className="ovw__panel-title">Predajnosť denného menu</h3>
          <button
            className="ovw__sort-btn"
            onClick={() => setSalesSortDir((d) => (d === "desc" ? "asc" : "desc"))}
            title={salesSortDir === "desc" ? "Zoradiť: Najmenej predávané" : "Zoradiť: Najviac predávané"}
          >
            <ArrowUpDown size={13} />
            <span>{salesSortDir === "desc" ? "Najviac predávané" : "Najmenej predávané"}</span>
          </button>
        </div>
        <div className="ovw__panel-body ovw__panel-body--sales">
          <div className="ovw__tabs">
            {SALES_TABS.map(({ type, label, Icon }) => (
              <button
                key={type}
                className={`ovw__tab${activeSalesTab === type ? " ovw__tab--active" : ""}`}
                onClick={() => setActiveSalesTab(type)}
              >
                <Icon size={14} />
                <span>{label}</span>
              </button>
            ))}
          </div>
          {loading ? (
            <div className="ovw__orders-loading">
              <Loader2 size={18} className="ovw__spinner" />
              <span>Načítavam…</span>
            </div>
          ) : currentSalesItems.length === 0 ? (
            <p className="ovw__orders-empty">
              Žiadne položky v tejto kategórii pre vybrané obdobie.
            </p>
          ) : (
            <div className="ovw__sales-scroll">
            <div className="ovw__sales-list">
              {sortedSalesItems.map((item) => {
                const dailyLimit = Number(item.limit) || 0;
                const barPct = Math.round((item.sold / maxSold) * 100);
                const nearLimit = dailyLimit > 0 && item.sold >= dailyLimit * 0.9;
                const fillMod = nearLimit ? " ovw__sales-bar-fill--warning" : "";
                return (
                  <div key={item.id} className="ovw__sales-item">
                    <div className="ovw__sales-item-header">
                      <span className="ovw__sales-name">
                        {item.code && (
                          <strong className="ovw__sales-code">{item.code}</strong>
                        )}
                        {item.name}
                      </span>
                      <span className="ovw__sales-stats ovw__sales-stats--prominent">
                        {item.sold} ks / {Number(item.revenue || 0).toFixed(2)} €
                      </span>
                    </div>
                    <div className="ovw__sales-bar-row">
                      <div className="ovw__sales-bar-track">
                        <div
                          className={`ovw__sales-bar-fill${fillMod}`}
                          style={{ width: `${barPct}%` }}
                        />
                      </div>
                      <span className="ovw__sales-limit">
                        {item.sold} / {dailyLimit > 0 ? dailyLimit : "∞"} limit
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Vývoj objednávok v čase ── */}
      <div className="ovw__panel ovw__panel--chart">
        <div className="ovw__panel-header">
          <h3 className="ovw__panel-title">Objednávky podľa hodiny</h3>
        </div>
        <div className="ovw__panel-body ovw__panel-body--chart">
          {loading ? (
            <div className="ovw__orders-loading">
              <Loader2 size={18} className="ovw__spinner" />
              <span>Načítavam…</span>
            </div>
          ) : hourlyData.length === 0 ? (
            <p className="ovw__orders-empty">Žiadne objednávky pre vybrané obdobie.</p>
          ) : (
            <div className="ovw__chart-bars">
              {hourlyData.map((d) => {
                const barPct = Math.round((d.orders / maxHourlyOrders) * 100);
                return (
                  <div key={d.hour} className="ovw__chart-col">
                    <div className="ovw__chart-bar-wrap">
                      <div
                        className="ovw__chart-bar-label"
                        style={{ bottom: `calc(${barPct}% + 6px)` }}
                      >
                        <span className="ovw__chart-bar-label-orders">{d.orders} obj.</span>
                        <span className="ovw__chart-bar-label-rev">{d.revenue.toFixed(0)} €</span>
                      </div>
                      <div
                        className="ovw__chart-bar"
                        style={{ height: `${barPct}%` }}
                      />
                    </div>
                    <span className="ovw__chart-label">{d.hour}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      </div>{/* /ovw__charts-grid */}

      {/* ── Zoznam objednávok ── */}
      <div className="ovw__panel">
        <div className="ovw__panel-header ovw__panel-header--with-action">
          <div className="ovw__header-main">
          {/* Row 1: Nadpis + Export button */}
          <div className="ovw__header-top">
            <h3 className="ovw__panel-title">Zoznam objednávok</h3>
            <button
              className="ovw__export-btn"
              onClick={handleExportToExcel}
              disabled={sortedOrders.length === 0 || loading}
              title="Exportovať do Excelu"
            >
              <FileDown size={15} />
              <span>Export do Excelu</span>
            </button>
          </div>
          
          {/* Row 2: Filtre (Payment filter + Search) */}
          <div className="ovw__header-filters">
            <div className="ovw__orders-payment-filter">
              <Select
                label="Typ platby"
                options={paymentFilterOptions}
                value={selectedPaymentFilter}
                onChange={setSelectedPaymentFilter}
              />
            </div>
            <div className="ovw__orders-payment-filter">
              <Select
                label="Zdroj"
                options={SOURCE_FILTER_OPTIONS}
                value={sourceFilter}
                onChange={setSourceFilter}
              />
            </div>
            <div className="ovw__orders-payment-filter">
              <Select
                label="Spôsob"
                options={DELIVERY_TYPE_FILTER_OPTIONS}
                value={deliveryTypeFilter}
                onChange={setDeliveryTypeFilter}
              />
            </div>
            <div className="ovw__orders-search" role="search">
              <Search size={14} className="ovw__orders-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="ovw__orders-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Hľadať meno, adresu, telefón..."
                aria-label="Hľadať objednávku podľa mena, adresy alebo telefónu"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="ovw__orders-search-clear"
                  onClick={() => setSearchQuery("")}
                  aria-label="Vymazať vyhľadávanie"
                  title="Vymazať vyhľadávanie"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
          </div>{/* /ovw__header-main */}

          {/* Row 3: Súhrn objednaných jedál za celé zobrazené obdobie */}
          <div className="ovw__items-summary">
            <span className="ovw__items-summary-title">Súhrn objednaných jedál</span>
            {loading ? (
              <span className="ovw__items-summary-empty">Načítavam…</span>
            ) : orderedItemsSummary.length === 0 ? (
              <span className="ovw__items-summary-empty">
                Žiadne objednané jedlá pre vybrané obdobie a filtre.
              </span>
            ) : (
              <div
                className="ovw__items-summary-chips ovw__items-summary-chips--clickable"
                onClick={() => setSummaryModalOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setSummaryModalOpen(true)}
                title="Zobraziť detail jedál"
              >
                {orderedItemsSummary.map((item) => (
                  <span key={item.key} className="order-card__item-chip">
                    {item.quantity}x{" "}
                    <strong
                      className={`order-card__item-code${!item.hasCode ? " order-card__item-code--truncated" : ""}`}
                      title={item.name}
                    >
                      {item.label}
                    </strong>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Detail jedál súhrnu – rovnaký modal ako pri objednávkach */}
          <OrderItemsModal
            isOpen={summaryModalOpen}
            onClose={() => setSummaryModalOpen(false)}
            order={orderedItemsSummaryOrder}
            title="Súhrn objednaných jedál"
            showComposedMenus={false}
          />
        </div>
        <div className="ovw__panel-body">
          {loading ? (
            <div className="ovw__orders-loading">
              <Loader2 size={18} className="ovw__spinner" />
              <span>Načítavam objednávky…</span>
            </div>
          ) : sortedOrders.length === 0 ? (
            <p className="ovw__orders-empty">Žiadne objednávky pre vybrané obdobie.</p>
          ) : filteredOrders.length === 0 ? (
            <p className="ovw__orders-empty">Pre zadané filtre sa nenašli žiadne objednávky.</p>
          ) : (
            <div className="ovw__orders-list">
              {filteredOrders.map((order) => (
                <ReadOnlyOrderCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </KpiCardFontProvider>
  );
}

export default OverviewSection;
